import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import * as int from "./users-groups.interface";
// Thunks para Usuarios
import * as thunks from './users-groupsThunks';
import { APIResponse } from '@/types/custom-http';


const initialState: int.UserGroupState = {
  groups: [],
  selectedUserGroup: null,
  relations: [],
  selectedRelation: null,
  loading: false,
  error: null,
};



// Slice
const usersGroupSlice = createSlice({
  name: 'usersGroups',
  initialState,
  reducers: {
    clearUsersGroup: (state) => {
      state.groups = [];
    },
    clearSelectedUserGroup: (state) => {
      state.selectedUserGroup = null;
    },
    clearRelations: (state) => {
      state.relations = [];
    },
    clearSelectedRelation: (state) => {
      state.selectedRelation = null;
    },
    clearUsersGroupsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.fetchUsersGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchUsersGroups.fulfilled, (state, action: PayloadAction<APIResponse<int.UsersGroups[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.groups = action.payload.data;
        }
      })
      .addCase(thunks.fetchUsersGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchUserGroupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchUserGroupById.fulfilled, (state, action: PayloadAction<APIResponse<int.UsersGroups>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedUserGroup = action.payload.data;
        }
      })
      .addCase(thunks.fetchUserGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.createUserGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createUserGroup.fulfilled, (state, action: PayloadAction<APIResponse<int.UsersGroups>>) => {
        state.loading = false;
        if(action.payload.data){
          state.groups.push(action.payload.data);
        }
      })
      .addCase(thunks.createUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.updateUserGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateUserGroup.fulfilled, (state, action: PayloadAction<APIResponse<int.UsersGroups>>) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.groups[index] = action.payload.data;
        }
        if (state.selectedUserGroup && state.selectedUserGroup.id === action.payload.data?.id) {
          state.selectedUserGroup = action.payload.data;
        }
      })
      .addCase(thunks.updateUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.deleteUserGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteUserGroup.fulfilled, (state, action) => {
        state.loading = false;
        const deletedUserId = action.payload;
        
        state.groups = state.groups.filter(group => group.id !== deletedUserId);
        
        if (state.selectedUserGroup && state.selectedUserGroup.id === deletedUserId) {
          state.selectedUserGroup = null;
        }
      })
      .addCase(thunks.deleteUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchGroupRoleModule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchGroupRoleModule.fulfilled, (state, action: PayloadAction<APIResponse<int.GroupRoleModule[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.relations = action.payload.data;
        }
      })
      .addCase(thunks.fetchGroupRoleModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchGroupRoleModuleByGroupId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchGroupRoleModuleByGroupId.fulfilled, (state, action: PayloadAction<APIResponse<int.GroupRoleModule[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.relations = action.payload.data;
        }
      })
      .addCase(thunks.fetchGroupRoleModuleByGroupId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.createGroupRoleModule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createGroupRoleModule.fulfilled, (state, action: PayloadAction<APIResponse<int.GroupRoleModule>>) => {
        state.loading = false;
        if(action.payload.data){
          state.relations.push(action.payload.data);
        }
      })
      .addCase(thunks.createGroupRoleModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.deleteGroupRoleModule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteGroupRoleModule.fulfilled, (state, action) => {
        state.loading = false;
        const deletedRelationId = action.payload;
        
        state.relations = state.relations.filter(relation => relation.id !== deletedRelationId);
        
        if (state.selectedUserGroup && state.selectedUserGroup.id === deletedRelationId) {
          state.selectedRelation = null;
        }
      })
      .addCase(thunks.deleteGroupRoleModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
      
  },
});

// Selectors
export const selectUsersGroups = (state: RootState) => state.usersGroups.groups;
export const selectUserGroupById = (state: RootState, id: number) => state.usersGroups.groups.find(group => group.id === id);
export const selectedUserGroup = (state: RootState) => state.usersGroups.selectedUserGroup;
export const selectRelationsGRM = (state: RootState) => state.usersGroups.relations;
export const selectRelationsGRMById = (state: RootState, id: number) => state.usersGroups.relations.find(rel => rel.id === id);
export const selectedRelationGRM = (state: RootState) => state.usersGroups.selectedRelation;
export const selectUsersGroupsLoading = (state: RootState) => state.users.loading;
export const selectUsersGroupsError = (state: RootState) => state.users.error;

// Actions
export const { clearUsersGroup, clearSelectedUserGroup,  clearRelations, clearSelectedRelation, clearUsersGroupsError} = usersGroupSlice.actions;

export default usersGroupSlice.reducer;